/**
 * ISSUE-1346. The "Invited pools" strip at the top of the Pools list, v1's
 * horizontally scrolling invited section above My pools. It shows only while
 * `/invite/list` has something waiting, reuses the Invite tab's own card
 * (Join opens the invite's preview there, Reject runs the same confirmed
 * decline), and See all goes to the Invite tab.
 */
import { useInviteList } from "@sportsmet/pool-sdk/private";
import { Link, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { InviteCard } from "./InviteCard";
import { resolveInvitesListState } from "./invitesListState";
import { useInviteRejectFlow } from "./useInviteRejectFlow";

export const INVITED_POOLS_TITLE = "Invited pools";
export const INVITED_POOLS_SEE_ALL = "See all";

export const InvitedPoolsStrip = (): ReactNode => {
  const navigate = useNavigate();
  const invites = useInviteList({});
  const items = invites.data?.data?.invites ?? null;
  const { rejected, rejectingId, handleReject } = useInviteRejectFlow(items);
  const state = resolveInvitesListState({
    isLoading: invites.isLoading,
    hasFailed: invites.isError || Boolean(invites.data?.error),
    items,
    rejected,
  });

  // Quiet on loading, failure and empty: the Invite tab owns those states,
  // and its badge already says when something is waiting.
  if (state.phase !== "ready") return null;
  const waitingCount = state.cards.filter(
    (card) => card.phase === "waiting",
  ).length;
  if (waitingCount === 0) return null;

  return (
    <section
      className="flex flex-col gap-2"
      aria-label={INVITED_POOLS_TITLE}
      data-testid="invited-pools-strip"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-body-strong text-ink">{`${INVITED_POOLS_TITLE} (${waitingCount})`}</h2>
        <Link
          to="/pools/invitations"
          className="inline-flex min-h-touch-target items-center text-body-strong text-primary"
          data-testid="invited-pools-see-all"
        >
          {INVITED_POOLS_SEE_ALL}
        </Link>
      </div>
      <div
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2"
        data-testid="invited-pools-row"
      >
        {state.cards.map((card) => (
          <div
            key={card.inviteId}
            className="w-72 max-w-full shrink-0 snap-start"
          >
            <InviteCard
              model={card}
              isRejecting={rejectingId === card.inviteId}
              onJoin={(inviteId) => {
                void navigate({
                  to: "/pools/invitations",
                  search: { inviteId },
                });
              }}
              onReject={(inviteId) => {
                void handleReject(inviteId);
              }}
            />
          </div>
        ))}
      </div>
    </section>
  );
};
